import { useLiveQuery } from "@tanstack/react-db";
import { useMemo } from "react";

import { enabledTagsQuery } from "@/models/repositories/Tag";
import { workspaceMembershipTagsQuery } from "@/models/repositories/WorkspaceMembershipTag";
import type { TagDefinition } from "@/types/driver";

// Stable fallback while the first run is pending: a literal `?? []` would change identity every
// render, recomputing `tags`/`tagValueMap` and everything memoized on them each render.
const NO_ROWS: never[] = [];

export function useDriverTags() {
  // Both live queries pre-filter disabled tags — they're kept in the store to track soft-deletes
  // across incremental syncs, but must never surface in the driver view.
  const { data: tagRows } = useLiveQuery(enabledTagsQuery);
  const { data: membershipTagRows } = useLiveQuery(workspaceMembershipTagsQuery);

  const { tags, tagValueMap } = useMemo(() => {
    const tagDefs: TagDefinition[] = (tagRows ?? []).map((t) => ({
      id: String(t.id),
      slug: String(t.slug),
      label: String(t.label),
      multiValue: Boolean(t.multiValue),
    }));

    const valueMap = new Map<string, Record<string, string[]>>();
    for (const row of membershipTagRows ?? []) {
      const membershipId = String(row.workspaceMembershipId);
      const tagId = String(row.tagId);
      const existing = valueMap.get(membershipId) ?? {};
      existing[tagId] = [...(existing[tagId] ?? []), String(row.tagValue)];
      valueMap.set(membershipId, existing);
    }

    return { tags: tagDefs, tagValueMap: valueMap };
  }, [tagRows, membershipTagRows]);

  return { tags, tagValueMap };
}
